import type {
  ServiceGetAllResponse,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  JenisKelompokDataType,
  JenisKelompokLandingResponseType,
  KelasPoktanDataType,
  KelasPoktanLandingResponseType,
  GapoktanPerProvinsiLandingResponseType,
  KepPerProvinsiLandingResponseType,
  PoktanPerProvinsiLandingResponseType,
  KelembagaanPenyuluhDataType,
  KelembagaanPenyuluhLandingDataType,
  KelembagaanPenyuluhLandingResponseType,
  KelembagaanPetaniDataType,
  KepBentukLandingResponseType,
  KepDataType,
  KetenagaanPenyuluhLandingDataType,
  KetenagaanPenyuluhLandingResponseType,
  KetenagaanPenyuluhPerProvinsiLandingResponseType,
  KetenagaPenyuluhanDataType,
  LuasLahanKomoditasType,
  LuasLahanProvinsiType,
  LuasLahanTerdataType,
  PetaniGenderType,
  PetaniJenisKelaminLandingResponseType,
  PetaniLandingDataType,
  PetaniTerdataType,
  PetaniTergabungType,
  PetaniTotalLandingResponseType,
  PetaniUsiaLandingResponseType,
  LandingSummaryApiResponse,
  SummaryDataType,
  UnitUsahaDataType,
  UsahaTaniDataType
} from './types'
import type { FilterKelembagaanValues } from './validation'

const LANDING_REQUEST_TIMEOUT_MS = 30_000
const LANDING_QUERY_RETRY = 3

const landingGet = <T>(url: string) =>
  api.get<T>(url, { timeout: LANDING_REQUEST_TIMEOUT_MS })

const withLandingQueryDefaults = <T>(
  options: ServiceQueryOptionType<T>
): ServiceQueryOptionType<T> => ({
  retry: LANDING_QUERY_RETRY,
  ...options
})

const KELEMBAGAAN_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['kelembagaan', key] as const
const KETENAGA_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['ketenaga', key] as const
const KELEMBAGAAN_PETANI_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['kelembagaan-petani', key] as const

const getAllKelembagaanPenyuluh = (
  params?: FilterKelembagaanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<KelembagaanPenyuluhDataType>>
> => ({
  queryKey: KELEMBAGAAN_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KelembagaanPenyuluhDataType>>
      >('/karyawan', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKetenagaPenyuluhan = (
  params?: FilterKelembagaanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<KetenagaPenyuluhanDataType>>
> => ({
  queryKey: KETENAGA_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KetenagaPenyuluhanDataType>>
      >('/karyawan', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKelembagaanPetani = (
  params?: FilterKelembagaanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<KelembagaanPetaniDataType>>
> => ({
  queryKey: KELEMBAGAAN_PETANI_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KelembagaanPetaniDataType>>
      >('/karyawan', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

// Landing Page API Functions
const getLandingSummary = (): ServiceQueryOptionType<SummaryDataType> =>
  withLandingQueryDefaults({
    queryKey: ['landing', 'summary'] as const,
    queryFn: async () => {
      try {
        const response = await landingGet<LandingSummaryApiResponse>(
          '/report/landing/summary'
        )

        return {
          reports: response.data.data,
          meta: response.data.meta,
          status: response.data.status,
          message: response.data.message
        }
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  })

const getLandingUnitUsaha = (): ServiceQueryOptionType<UnitUsahaDataType> =>
  withLandingQueryDefaults({
    queryKey: ['landing', 'gapoktan', 'unit-usaha'] as const,
    queryFn: async () => {
      try {
        const response = await landingGet<{ data: UnitUsahaDataType }>(
          '/report/landing/gapoktan/unit-usaha'
        )
        return response.data.data
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  })

const getLandingUsahaTani = (): ServiceQueryOptionType<UsahaTaniDataType> =>
  withLandingQueryDefaults({
    queryKey: ['landing', 'gapoktan', 'usaha-tani'] as const,
    queryFn: async () => {
      try {
        const response = await landingGet<{ data: UsahaTaniDataType }>(
          '/report/landing/gapoktan/usaha-tani'
        )
        return response.data.data
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  })

const getLandingKelembagaanPenyuluh =
  (): ServiceQueryOptionType<KelembagaanPenyuluhLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'kelembagaan-penyuluh'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{
            data: KelembagaanPenyuluhLandingDataType
            status: boolean
            message: string
            meta: KelembagaanPenyuluhLandingResponseType['meta']
          }>('/report/landing/kelembagaan-penyuluh')

          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingKetenagaanPenyuluh =
  (): ServiceQueryOptionType<KetenagaanPenyuluhLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'ketenagaan-penyuluh'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{
            data: KetenagaanPenyuluhLandingDataType
            status: boolean
            message: string
            meta: KetenagaanPenyuluhLandingResponseType['meta']
          }>('/report/landing/ketenagaan-penyuluh')

        return {
          data: response.data.data,
          meta: response.data.meta,
          status: response.data.status,
          message: response.data.message
        }
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  })

const getLandingKetenagaanPenyuluhPerProvinsi =
  (): ServiceQueryOptionType<KetenagaanPenyuluhPerProvinsiLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'ketenagaan-penyuluh', 'per-provinsi'] as const,
      queryFn: async () => {
        try {
          const response =
            await landingGet<KetenagaanPenyuluhPerProvinsiLandingResponseType>(
              '/report/landing/ketenagaan-penyuluh/per-provinsi'
            )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingJenisKelompok =
  (): ServiceQueryOptionType<JenisKelompokLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'poktan', 'jenis-kelompok'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<JenisKelompokLandingResponseType>(
            '/report/landing/poktan/jenis-kelompok'
          )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingKelasPoktan =
  (): ServiceQueryOptionType<KelasPoktanLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'poktan', 'kelas-poktan'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<KelasPoktanLandingResponseType>(
            '/report/landing/poktan/kelas-poktan'
          )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingPoktanPerProvinsi =
  (): ServiceQueryOptionType<PoktanPerProvinsiLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'poktan', 'per-provinsi'] as const,
      queryFn: async () => {
        try {
          const response =
            await landingGet<PoktanPerProvinsiLandingResponseType>(
              '/report/landing/poktan/per-provinsi'
            )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingGapoktanPerProvinsi =
  (): ServiceQueryOptionType<GapoktanPerProvinsiLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'gapoktan', 'per-provinsi'] as const,
      queryFn: async () => {
        try {
          const response =
            await landingGet<GapoktanPerProvinsiLandingResponseType>(
              '/report/landing/gapoktan/per-provinsi'
            )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingKepPerProvinsi =
  (): ServiceQueryOptionType<KepPerProvinsiLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'kep', 'per-provinsi'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<KepPerProvinsiLandingResponseType>(
            '/report/landing/kep/per-provinsi'
          )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingKep = (): ServiceQueryOptionType<KepDataType> =>
  withLandingQueryDefaults({
    queryKey: ['landing', 'kep'] as const,
    queryFn: async () => {
      try {
        const response = await landingGet<{ data: KepDataType }>(
          '/report/landing/kep'
        )
        return response.data.data
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  })

const getLandingKepBentuk =
  (): ServiceQueryOptionType<KepBentukLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'kep', 'bentuk-kep'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<KepBentukLandingResponseType>(
            '/report/landing/kep/bentuk-kep'
          )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingPetani = (): ServiceQueryOptionType<PetaniLandingDataType> =>
  withLandingQueryDefaults({
    queryKey: ['landing', 'petani'] as const,
    queryFn: async () => {
      try {
        const response = await landingGet<{ data: PetaniLandingDataType }>(
          '/report/landing/petani'
        )
        return response.data.data
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  })

const getLandingPetaniTotal =
  (): ServiceQueryOptionType<PetaniTotalLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'total'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<PetaniTotalLandingResponseType>(
            '/report/landing/petani/total'
          )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingPetaniTerdata =
  (): ServiceQueryOptionType<PetaniTerdataType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'terdata'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{ data: PetaniTerdataType }>(
            '/report/landing/petani/terdata'
          )
          return response.data.data
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingPetaniTergabung =
  (): ServiceQueryOptionType<PetaniTergabungType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'tergabung'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{ data: PetaniTergabungType }>(
            '/report/landing/petani/tergabung'
          )
          return response.data.data
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingLuasLahanTerdata =
  (): ServiceQueryOptionType<LuasLahanTerdataType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'luas-lahan-terdata'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{ data: LuasLahanTerdataType }>(
            '/report/landing/petani/luas-lahan-terdata'
          )
          return response.data.data
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingPetaniUsia =
  (): ServiceQueryOptionType<PetaniUsiaLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'usia'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<PetaniUsiaLandingResponseType>(
            '/report/landing/petani/usia'
          )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingPetaniJenisKelamin =
  (): ServiceQueryOptionType<PetaniJenisKelaminLandingResponseType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'jenis-kelamin'] as const,
      queryFn: async () => {
        try {
          const response =
            await landingGet<PetaniJenisKelaminLandingResponseType>(
              '/report/landing/petani/jenis-kelamin'
            )
          return {
            data: response.data.data,
            meta: response.data.meta,
            status: response.data.status,
            message: response.data.message
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingPetaniGender =
  (): ServiceQueryOptionType<PetaniGenderType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'gender'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{
            data: Array<{ gender: string; total: number; label: string }>
            label: string
            status: boolean
          }>('/report/landing/petani/gender')
          return {
            data: response.data.data,
            label: response.data.label
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingLuasLahanProvinsi =
  (): ServiceQueryOptionType<LuasLahanProvinsiType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'luas-lahan-provinsi'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{
            data: Array<{
              kode_provinsi: string
              nama_provinsi: string
              luas_lahan: number
            }>
            label: string
            status: boolean
          }>('/report/landing/petani/luas-lahan-provinsi')
          return {
            data: response.data.data,
            label: response.data.label
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

const getLandingLuasLahanKomoditas =
  (): ServiceQueryOptionType<LuasLahanKomoditasType> =>
    withLandingQueryDefaults({
      queryKey: ['landing', 'petani', 'luas-lahan-komoditas'] as const,
      queryFn: async () => {
        try {
          const response = await landingGet<{
            subsektor_list: string[]
            data: Array<{
              nama_komoditas: string
              subsektor: string
              luas_lahan: number
            }>
            label: string
            description: string
            status: boolean
          }>('/report/landing/petani/luas-lahan-komoditas')
          return {
            data: response.data.data,
            subsektor_list: response.data.subsektor_list,
            label: response.data.label,
            description: response.data.description
          }
        } catch (error) {
          throw handleAxiosError(error)
        }
      }
    })

export const home = {
  getAllKelembagaanPenyuluh,
  getAllKetenagaPenyuluhan,
  getAllKelembagaanPetani,
  getLandingSummary,
  getLandingUnitUsaha,
  getLandingUsahaTani,
  getLandingKelembagaanPenyuluh,
  getLandingKetenagaanPenyuluh,
  getLandingKetenagaanPenyuluhPerProvinsi,
  getLandingJenisKelompok,
  getLandingKelasPoktan,
  getLandingPoktanPerProvinsi,
  getLandingGapoktanPerProvinsi,
  getLandingKep,
  getLandingKepBentuk,
  getLandingKepPerProvinsi,
  getLandingPetani,
  getLandingPetaniTotal,
  getLandingPetaniTerdata,
  getLandingPetaniTergabung,
  getLandingLuasLahanTerdata,
  getLandingPetaniUsia,
  getLandingPetaniJenisKelamin,
  getLandingPetaniGender,
  getLandingLuasLahanProvinsi,
  getLandingLuasLahanKomoditas
}
